import axios from 'axios';
import {
    OIDC_TOKEN_URL,
    CLIENT_ID,
    CLIENT_SECRET,
    decodeJwtPayload,
    persistAuthArtifacts,
    clearAuthArtifacts,
    parseAuToken,
} from './oidcTokenStorage';
import { isLocalhostWithDevToken } from './envFromCookies';

const EXPIRY_BUFFER_SECONDS = 30;

export const getAccessTokenExpMs = (accessToken) => {
    const payload = decodeJwtPayload(accessToken);
    if (!payload || typeof payload.exp !== 'number') return 0;
    return payload.exp * 1000;
};

export const isAccessTokenExpired = (
    accessToken,
    bufferSeconds = EXPIRY_BUFFER_SECONDS
) => {
    if (!accessToken) {
        console.log('[OIDC] isAccessTokenExpired: no access_token -> treating as expired');
        return true;
    }
    const expMs = getAccessTokenExpMs(accessToken);
    if (!expMs) {
        console.log('[OIDC] isAccessTokenExpired: could not decode exp -> treating as expired');
        return true;
    }
    const expired = Date.now() + bufferSeconds * 1000 >= expMs;
    const secondsLeft = Math.floor((expMs - Date.now()) / 1000);
    console.log(`[OIDC] isAccessTokenExpired: secondsLeft=${secondsLeft}, expired=${expired}`);
    return expired;
};

let pendingRefresh = null;

const handleRefreshFailure = (reason) => {
    console.error('OIDC refresh failed:', reason);
    try { clearAuthArtifacts(); } catch (e) { /* noop */ }
    try {
        if (typeof window !== 'undefined') {
            window.location.href = '/';
        }
    } catch (e) { /* noop */ }
};

const doRefresh = async () => {
    console.log('[OIDC] doRefresh: starting silent refresh...');
    const bundle = parseAuToken();
    if (!bundle) {
        console.warn('[OIDC] doRefresh: no _au token in storage');
        handleRefreshFailure('no _au token in storage');
        throw new Error('No auth token to refresh');
    }
    const refreshToken = bundle.refresh_token;
    if (!refreshToken) {
        console.warn('[OIDC] doRefresh: refresh_token missing in _au bundle');
        handleRefreshFailure('refresh_token missing in _au');
        throw new Error('No refresh_token available');
    }
    console.log('[OIDC] doRefresh: refresh_token present, posting to', OIDC_TOKEN_URL);

    const refreshData = {
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        refresh_token: refreshToken,
    };

    let response;
    try {
        response = await axios.post(OIDC_TOKEN_URL, refreshData, {
            _skipAuthRefresh: true,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
    } catch (err) {
        console.error('[OIDC] doRefresh: network/HTTP error', err && err.response && err.response.status, err && err.response && err.response.data);
        handleRefreshFailure(err);
        throw err;
    }

    const data = response && response.data;
    if (!data || !data.access_token) {
        console.error('[OIDC] doRefresh: response missing access_token', data);
        handleRefreshFailure('refresh response missing access_token');
        throw new Error('Refresh response missing access_token');
    }

    if (!data.refresh_token && bundle.refresh_token) {
        console.log('[OIDC] doRefresh: provider did not rotate refresh_token, reusing existing');
        data.refresh_token = bundle.refresh_token;
    }

    const newExpMs = getAccessTokenExpMs(data.access_token);
    console.log('[OIDC] doRefresh: SUCCESS. New access_token expires at', newExpMs ? new Date(newExpMs).toLocaleString() : 'unknown');
    persistAuthArtifacts(data);
    console.log('[OIDC] doRefresh: new tokens persisted to cookie + localStorage');
    return data;
};

export const refreshAccessToken = () => {
    if (pendingRefresh) {
        console.log('[OIDC] refreshAccessToken: refresh already in-flight, awaiting existing promise');
        return pendingRefresh;
    }
    console.log('[OIDC] refreshAccessToken: kicking off new refresh');
    pendingRefresh = doRefresh().finally(() => {
        pendingRefresh = null;
    });
    return pendingRefresh;
};

export const ensureFreshToken = async () => {
    if (isLocalhostWithDevToken()) {
        return;
    }
    const bundle = parseAuToken();
    if (!bundle || !bundle.access_token) {
        console.log('[OIDC] ensureFreshToken: no token in storage, skipping');
        return;
    }
    if (!isAccessTokenExpired(bundle.access_token)) return;
    console.log('[OIDC] ensureFreshToken: token expired/expiring, triggering refresh');
    await refreshAccessToken();
};
